import { Layout, Table, Typography } from "antd";
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react";
import { useDpatIndicatorDataSource } from "hooks/useDpatIndicatorDataSource";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import MaxScoreDisplay from "../setup/MaxScoreDisplay";
import ActualScoreDisplay from "../setup/ActualScoreDisplay";
import IndicatorScoreSection from "../setup/IndicatorScoreSection";
import Comment from "components/Comments";
import axios from "api/axios";
import { formatDataGeneral, getAttributeValue, getFileLinkIfExist } from "utils/utils";
import { renderDpatEvidenceLink, scoreOnlyIfDocumentLinksComplete } from "utils/renderDpatEvidenceLink";
import { documentsColumn } from "utils/tableColums";

const PUBLICATION_FILE_ELEMENT = "xjRCTFFiMA3";
const DOCUMENT_HUB_FILE_ELEMENT = "MjPmcbueEdU";

function fileUrl(eventId, dataElementUid) {
  return `https://dddp.gov.gh/api/events/files?eventUid=${eventId}&dataElementUid=${dataElementUid}`;
}

function buildClientServiceCharterDocLinks(documents) {
  const documentData = documents?.data;
  const documentReports = documents?.reports;
  const charterDocs = formatDataGeneral(
    documentData,
    "DPAT | M&E Tool Type",
    "Approved Assemblies Client Service Charter",
  ) || [];

  let charterLink = "";
  if (charterDocs.length > 0) {
    charterLink = getFileLinkIfExist(
      documentReports,
      DOCUMENT_HUB_FILE_ELEMENT,
      charterDocs[0]?.trackedEntity,
    );
  }

  return [
    {
      title: "Approved Assemblies Client Service Charter",
      link: charterLink
        ? fileUrl(charterLink, DOCUMENT_HUB_FILE_ELEMENT)
        : "Not Uploaded",
    },
  ];
}

const ClientServiceCharter = forwardRef(({
  year,
  districtId,
  documents,
  hideComment,
  savedAssessmentData,
  assessmentStartLoading,
  isAssessmentStarted,
}, ref) => {
  const { Content } = Layout;
  const { Title } = Typography;
  const [clientServiceCharter, setClientServiceCharter] = useState([]);
  const [documentLinks, setDocumentLinks] = useState([]);
  const [score, setScore] = useState(0);
  const [maxScore, setMaxScore] = useState(1);

  const DPAT_SLUG = "sdi2.0-2.5";

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "SDI2",
      area: "Basic/ Social Services",
      maxScore,
      clientServiceCharter,
      links: documentLinks,
      score,
    }),
  }));

  const permitRequestColumn = [
    { title: "Client Service Charter Availability (YES/NO)", dataIndex: "availability", key: "availability" },
    { title: "Date Approved", dataIndex: "approvalDate", key: "approvalDate" },
    { title: "Document Reference", dataIndex: "docReference", key: "docReference" },
    { title: "Web site", dataIndex: "webSiteLink", key: "webSiteLink" },
    { title: "Channel", dataIndex: "channel", key: "channel" },
    {
      title: "Document Report",
      dataIndex: "document",
      key: "document",
      render: (value) => renderDpatEvidenceLink(value, "View Evidence"),
    },
  ];

  const documentTableColumns = useMemo(
    () => documentsColumn.map((col) =>
      col.dataIndex === "link"
        ? {
          ...col,
          render: (value) => renderDpatEvidenceLink(value, "View Document"),
        }
        : col
    ),
    [],
  );

  const loadFromDhis2 = useCallback(() => {
    const links = buildClientServiceCharterDocLinks(documents);
    setDocumentLinks(links);

    axios
      .get(`/tracker/trackedEntities?orgUnit=${districtId}&program=X5kGqVpbGoN`)
      .then((result) => {
        if (result.data.instances.length === 0) {
          setClientServiceCharter([]);
          setScore(0);
          return;
        }

        axios
          .get(`/tracker/events?program=X5kGqVpbGoN&orgUnit=${districtId}`)
          .then((resp) => {
            const data = formatDataGeneral(result.data.instances, "Document Published", "Client Service Charter") || [];
            const reports = resp.data.instances;
            const temp = [];
            let tempScore = 1;

            data.forEach((document, index) => {
              const reportLink = getFileLinkIfExist(reports, PUBLICATION_FILE_ELEMENT, document.trackedEntity);

              const tempDataSet = {
                key: document.trackedEntity || index + 1,
                availability: reportLink ? "YES" : "NO",
                approvalDate: getAttributeValue("Document Approval Date", document),
                docReference: getAttributeValue("Document Reference ", document),
                channel: getAttributeValue("Publication Channel", document),
                webSiteLink: getAttributeValue("Website", document),
                document: reportLink
                  ? fileUrl(reportLink, PUBLICATION_FILE_ELEMENT)
                  : "Not Uploaded",
              };

              temp.push(tempDataSet);

              if (!reportLink) {
                tempScore = 0;
              }
            });

            if (temp.length === 0) {
              tempScore = 0;
            }

            setScore(scoreOnlyIfDocumentLinksComplete(links, tempScore));
            setClientServiceCharter(temp);
          })
          .catch((err) => console.log(err));
      })
      .catch((err) => console.log("decisions error ", err));
  }, [districtId, documents]);

  useDpatIndicatorDataSource({
    isAssessmentStarted,
    savedAssessmentData,
    assessmentStartLoading,
    loadFromDhis2,
    applySnapshot: (snap) => {
      if (snap.clientServiceCharter) setClientServiceCharter(snap.clientServiceCharter);
      if (snap.links) setDocumentLinks(snap.links);
      if (snap.score != null) setScore(snap.score);
      if (snap.maxScore != null) setMaxScore(snap.maxScore);
    },
    deps: [year, districtId, documents, loadFromDhis2],
  });

  return (
    <Comment
      data={clientServiceCharter}
      year={year}
      districtId={districtId}
      tableCommentedId={`sdi2.0-2.5-${year}`}
      hideComment={hideComment}
    >
      {({ renderCommentFeedbackFooter }) => (
          <DpatSubIndicatorSection code={DPAT_SLUG}>
          <DpatIndicatorCmsIntro
            year={year}
            slug={DPAT_SLUG}
            mainTitleStyle={{ marginTop: "30px" }}
          />

          <IndicatorScoreSection>
          <MaxScoreDisplay score={maxScore} />
                    <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 2.0-2.5 Actual Score:" score={score} />
          </IndicatorScoreSection>
          <Title level={4} style={{ marginTop: "30px" }}>Findings / Observations & Conclusion</Title>
          <Content>
            <p>We received and reviewed information on the Client Service Charter from the MCD and notes as follows:</p>
            <p>The minutes of the GA meeting for the approval of the Client Service Charter, signed by the PM and MCD, was reviewed as follows:</p>
          </Content>

          {clientServiceCharter?.length > 0 && (
            <Table
              columns={permitRequestColumn}
              dataSource={clientServiceCharter}
              rowKey="key"
              pagination={false}
              bordered
            />
          )}

          <Title level={5} style={{ marginTop: "20px" }}>Client service Approval Evidence</Title>
          <Table
            columns={documentTableColumns}
            dataSource={documentLinks}
            rowKey="title"
            pagination={false}
            bordered
          />

            {!hideComment && renderCommentFeedbackFooter()}
          </DpatSubIndicatorSection>
      )}
    </Comment>
  );
});

export default ClientServiceCharter;
